import { useEffect, useState } from "react";

import { computeDaysSince } from "@jti/fe-common";

import { useLastSuccessfulPurchaseDate } from "./useLastSuccessfulPurchaseDate";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// This function checks if the last purchase date is within the purchasedWithinDays threshold
// Returns true if the purchase was made in less than the specified number of days, false otherwise
const isPurchasedDateTooSoon = (purchasedWithinDays: number, lastPurchaseDate?: Date | null) => {
  if (lastPurchaseDate) {
    const timeAgoInDays = (Date.now() - Number(lastPurchaseDate.getTime())) / MS_PER_DAY;
    if (timeAgoInDays < purchasedWithinDays) {
      return true;
    }
  }

  return false;
};

// This hook determines if the personalization modal can be opened if isPurchasedXDaysAgoEnabled is enabled, and based on the last successful purchase date and the purchasedWithinDays threshold
export function usePurchasedXDaysAgo(isPurchasedXDaysAgoEnabled: boolean, purchasedWithinDays: number) {
  const [canOpenModal, setCanOpenModal] = useState(!isPurchasedXDaysAgoEnabled);

  const { lastSuccessfulPurchaseDate, isLoading } = useLastSuccessfulPurchaseDate({
    enabled: isPurchasedXDaysAgoEnabled,
  });

  useEffect(() => {
    if (!isPurchasedXDaysAgoEnabled) {
      setCanOpenModal(true);
      return;
    }

    if (isLoading) {
      return;
    }

    if (isPurchasedDateTooSoon(purchasedWithinDays, lastSuccessfulPurchaseDate)) {
      setCanOpenModal(false);
      return;
    }

    const daysSince = computeDaysSince(lastSuccessfulPurchaseDate);

    if (daysSince !== null && daysSince >= purchasedWithinDays) {
      setCanOpenModal(true);
      return;
    }

    setCanOpenModal(false);
  }, [isPurchasedXDaysAgoEnabled, lastSuccessfulPurchaseDate, purchasedWithinDays, isLoading]);

  return canOpenModal;
}
